import React, { useEffect, useState } from "react"
import ProfileTabs from "../components/ProfileTabs"
import { useAuth } from "../context/authContext"
import { getSentOffersByUserId, getReceivedOffersByUserId, updateOffer } from "../firebase/offers"
import { formatDate } from "../utils"
import { Link } from "react-router-dom"
import DialogConfirmation from "../components/dialogs/DialogConfirmation"
import Pagination from "../components/commons/Pagination"
import axios from "axios"

import { createCheckouts } from "../firebase/payments"
import { getAuthHeaders } from "../firebase/authHeaders"
import { useAtom } from "jotai"
import { userAtom } from "../store"
import ProfileTitle from "../components/ProfileTitle"
import DialogLoading from "../components/dialogs/DialogLoading"
import { db } from "../firebase/config"
import { addRecipient } from "../firebase/users"
import { addDoc, collection } from "firebase/firestore"
import { cancelOffer } from "../firebase/offers"

const fields = [
    "Title", "Offer Date", "Offer Price", "Original Price", "Other"
]

const itemsPerPage = 4

const SentItems = ({ items, reload }) => {
    const [userData, setUserData] = useAtom(userAtom)
    const [currentPage, setCurrentPage] = useState(1)
    const [currentMobilePage, setCurrentMobilePage] = useState(1)
    const [loading, setLoading] = useState(false)
    const [selectedItem, setSelectedItem] = useState(null)
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [showCancelSuccessModal, setShowCancelSuccessModal] = useState(false)

    const onOfferCancel = (item) => {
        setSelectedItem(item)
        setShowConfirmModal(true)
    }
    const confirmCancellation = ()=>{
        const item = selectedItem;
        console.log("cancel", item);
        if(item.state !== "pending")
            return;
        cancelOffer(item)
            .then(()=>{
                reload && reload()
            })
            .catch((_)=>{
                alert("Offer cancel failed");
            })
            .finally(()=>{
                setShowConfirmModal(false);
                setShowCancelSuccessModal(true);
            })
    }
    const checkout = async (item) => {
        setLoading(true)

        // Check if PayPal is selected (you'll need to add payment method selection to offers)
        const hasPayPal = false; // For now, default to Stripe. You can add payment method selection later
        
        if (hasPayPal) {
            // Use PayPal direct checkout. Like the Stripe branch below, the
            // server charges the offer's stored offer_price -- the amount is
            // no longer sent from here.
            const paypalAuthHeaders = await getAuthHeaders();

            axios({
                method: 'post',
                url: process.env.REACT_APP_PAYMENT_API + '/create-paypal-checkout',
                headers: paypalAuthHeaders,
                data: {
                    prevLink: window.location.href,
                    successLink: `${window.location.origin}/success`,
                    name: "CardMarketPlace Checkout for Offer",
                    offerId: item.id
                }
            }).then(async ({ data }) => {
                if (data.success) {
                    await createCheckouts({ 
                        sessionId: data.paypalOrderId, 
                        sessionLink: data.approvalUrl, 
                        checkoutInfo: { type: 'offer', offer_id: item.id, payment_method: 'paypal' } 
                    });
                    
                    // Redirect to PayPal for payment
                    window.location.href = data.approvalUrl;
                    setLoading(false);
                } else {
                    throw new Error(data.error || 'PayPal checkout failed');
                }
            }).catch((error) => {
                console.error('PayPal checkout error:', error);
                setLoading(false);
                alert('PayPal checkout failed. Please try a different payment method.');
            });
        } else {
            // Use Stripe checkout. The server looks up this offer in
            // Firestore and charges its stored offer_price -- it no longer
            // trusts a client-supplied amount.
            const authHeaders = await getAuthHeaders();

            axios({
                method: 'post',
                url: process.env.REACT_APP_PAYMENT_API + '/create-checkout',
                headers: authHeaders,
                data: {
                    customerId: userData?.customer_id,
                    prevLink: window.location.href,
                    paymentMethodTypes: ["card"],
                    name: "CardMarketPlace Checkout for Offer",
                    offerId: item.id
                }
            }).then(async ({ data }) => {
                await createCheckouts({ sessionId: data.session.id, sessionLink: data.session.url, checkoutInfo: { type: 'offer', offer_id: item.id } })
                window.location.href = data.session.url;
                setLoading(false)
            }).catch((error) => {
                console.error('Checkout error:', error);
                setLoading(false);
                
                // Handle specific payment method errors
                if (error.response?.data?.error) {
                    const errorMessage = error.response.data.error;
                    if (errorMessage.includes('Bank transfer')) {
                        alert('Bank transfer is not available. Please use Card payment instead.');
                    } else {
                        alert('Checkout failed: ' + errorMessage);
                    }
                } else {
                    alert('Checkout failed. Please try again.');
                }
            })
        }
    }

    return (
        items.length > 0 ?
            <>
                {
                    selectedItem &&
                    <DialogConfirmation
                        message={`Yes, I want to cancel the offer: ${selectedItem.title}`}
                        open={showConfirmModal}
                        onButtonClick={confirmCancellation}
                        onClose={() => setShowConfirmModal(false)}
                        buttonText="Cancel Offer"
                        title="Do you want to cancel the offer?"
                        iconSrc="/assets/icons/icon-info.svg"
                        type="confirm"
                    />
                }

                {
                    selectedItem &&
                    <DialogConfirmation
                        message={`Successfully canceled the offer: ${selectedItem.title}`}
                        open={showCancelSuccessModal}
                        onClose={() => setShowCancelSuccessModal(false)}
                        buttonText="Done"
                        title="Offer Canceled!"
                        iconSrc="/assets/icons/icon-check-circle.svg"
                        type="confirm"
                    />
                }
                
                {
                    loading &&
                    <DialogLoading />
                }
                <div className="max-lg:hidden">
                    <div className="w-full overflow-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-[#2E2E2E] border-[1px] border-gray py-3 text-sm text-[#717171]">
                                    {
                                        fields.map((item, index) =>
                                            <th className={`py-3 text-left ${index > 0 ? '' : 'pl-3'}`} key={`offer-field-${item}`}>
                                                {item}
                                            </th>
                                        )
                                    }
                                </tr>
                            </thead>

                            <div className="pt-1" />
                            <tbody className="text-white space-y-1 text-sm">
                                {
                                    items.slice(itemsPerPage * (currentPage - 1), itemsPerPage * currentPage).map((item, index) =>
                                        <tr key={`offer-item-sent-${currentPage}-${item.title}-${item.id}-${index}`} className="border-[1px] border-gray text-nowrap">
                                            <td className="py-2 pl-3 pr-20 lg:min-w-[400px] min-w-[200px]">
                                                <div className="flex items-center">
                                                    <img
                                                        src={item.picture}
                                                        alt="picture"
                                                        className="w-[50px] h-[50px] mr-6 border-[1px] border-gray"
                                                    />

                                                    <span className="uppercase text-sm">
                                                        {item.title}
                                                    </span>
                                                </div>
                                            </td>

                                            <td className="lg:min-w-[200px] pr-10">
                                                {formatDate(item.created_at.seconds)}
                                            </td>

                                            <td className="lg:min-w-[200px] pr-10">
                                                $ {item.offer_price.toFixed(2)}
                                            </td>

                                            <td className="text-primary lg:min-w-[200px] pr-10">
                                                $ {item.original_price.toFixed(2)}
                                            </td>

                                            <td className="pr-3">
                                                {
                                                    item.state === 'pending' &&
                                                    <div className="flex">
                                                        <Link className="solo-button flex items-center justify-center mr-2 text-white border-white border-[1px] bg-primary w-[170px] h-[45px]" to={`/marketplace/${item.cardId}`}>
                                                            View Listing
                                                        </Link>

                                                        <button className="text-white border-[1px] border-gray  w-[100px] h-[45px]" disabled>
                                                            Offer Pending
                                                        </button>

                                                        <button className="text-red border-[1px] border-red ml-2 w-[100px] h-[45px]" onClick={()=>onOfferCancel(item)}>
                                                            Cancel
                                                        </button>
                                                    </div>
                                                }

                                                {
                                                    item.state === 'accepted' &&
                                                    <div className="flex">
                                                        <button className="solo-button text-white mr-2 border-[1px] border-white  w-[170px] h-[45px]" onClick={() => checkout(item)}>
                                                            Checkout Now
                                                        </button>

                                                        <button className="text-[#3CBE2E] w-[170px] h-[45px] bg-black border-[1px] border-[#3CBE2E]" disabled>
                                                            Offer Accepted
                                                        </button>
                                                    </div>
                                                }

                                                {
                                                    item.state === 'declined' &&
                                                    <div className="flex">
                                                        <Link className="solo-button flex items-center justify-center mr-2 text-white border-white border-[1px] bg-primary w-[170px] h-[45px]" to={`/marketplace/${item.cardId}`}>
                                                            View Listing
                                                        </Link>

                                                        <button className="text-red border-[1px] border-red  w-[170px] h-[45px]" disabled>
                                                            Offer Declined
                                                        </button>
                                                    </div>
                                                }

                                                {
                                                    item.state === 'completed' &&
                                                    <div className="flex">
                                                        <Link className="solo-button flex items-center justify-center mr-2 text-white border-white border-[1px] bg-primary w-[170px] h-[45px]" to={`/marketplace/${item.cardId}`}>
                                                            View Listing
                                                        </Link>

                                                        <button className="text-[#3CBE2E] border-[1px] border-[#3CBE2E]  w-[170px] h-[45px]" disabled>
                                                            Offer Completed
                                                        </button>
                                                    </div>
                                                }

                                                {
                                                    item.state === 'cancelled' && 
                                                    <div className="flex">
                                                        <Link className="solo-button flex items-center justify-center mr-2 text-white border-white border-[1px] bg-primary w-[170px] h-[45px]" to={`/marketplace/${item.cardId}`}>
                                                            View Listing
                                                        </Link>

                                                        <button className="text-[#9CA3AF] border-[1px] border-[#9CA3AF]  w-[170px] h-[45px]" disabled>
                                                            Offer Cancelled
                                                        </button>
                                                    </div>
                                                }
                                            </td>
                                        </tr>
                                    )
                                }
                            </tbody>
                        </table>
                    </div >

                    <div className="mt-9">
                        <Pagination itemsPerPage={itemsPerPage} totalItems={items.length} onPageChange={(value) => setCurrentPage(value)} />
                    </div>
                </div>

                <div className="lg:hidden">
                    <div className="grid sm:grid-cols-2 grid-cols-1 gap-5">
                        {
                            items.slice(3 * (currentMobilePage - 1), 3 * currentMobilePage).map(item =>
                                <div className="p-[10px] border-[1px] border-gray bg-[#191919]" key={`${item.id}-${currentMobilePage}-mobile`}>
                                    <div className="flex w-full">
                                        <img src={item.picture} className="w-[113px] h-[113px] object-cover border-[1px] border-gray mr-4" alt="image" />

                                        <div className="w-full mb-9">
                                            <div className="uppercase text-white text-sm">{item.title}</div>

                                            <div className="mt-4 w-full flex justify-between text-[#717171] text-xs">
                                                <span>Offer Price</span>
                                                <span className="text-white">$ {item.offer_price.toFixed(2)}</span>
                                            </div>

                                            <div className="mt-3 w-full flex justify-between text-[#717171] text-xs">
                                                <span>Original Price</span>
                                                <span className="text-white">$ {item.original_price.toFixed(2)}</span>
                                            </div>

                                            <div className="mt-3 w-full flex justify-between text-[#717171] text-xs">
                                                <span>Offer Date</span>
                                                <span className="text-white">{formatDate(item.created_at.seconds)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {
                                        item.state === 'pending' &&
                                        <div className="w-full text-sm">
                                            <button className="text-white border-[1px] border-gray w-full h-[40px]" disabled>
                                                Offer Pending
                                            </button>

                                            <Link className="flex mt-3 items-center justify-center mr-2 text-white border-white border-[1px] bg-primary w-full h-[40px]" to={`/marketplace/${item.cardId}`}>
                                                View Listing
                                            </Link>
                                        </div>
                                    }

                                    {
                                        item.state === 'accepted' &&
                                        <div className="w-full text-sm">

                                            <button className="text-[#3CBE2E] w-full h-[40px] bg-black border-[1px] border-[#3CBE2E]" disabled>
                                                Offer Accepted
                                            </button>
                                            <button className="text-white mt-3 mr-2 border-[1px] border-white w-full h-[40px]">
                                                Checkout Now
                                            </button>
                                        </div>
                                    }

                                    {
                                        item.state === 'declined' &&
                                        <div className="w-full text-sm">

                                            <button className="text-red border-[1px] border-red w-full h-[40px]" disabled>
                                                Offer Declined
                                            </button>
                                            <Link className="flex mt-3 items-center justify-center mr-2 text-white border-white border-[1px] bg-primary w-full h-[40px]" to={`/marketplace/${item.cardId}`}>
                                                View Listing
                                            </Link>
                                        </div>
                                    }

                                    {
                                        item.state === 'completed' &&
                                        <div className="w-full text-sm">
                                            <button className="text-[#3CBE2E] border-[1px] border-[#3CBE2E] w-full h-[40px]" disabled>
                                                Offer Completed
                                            </button>
                                            <Link className="flex mt-3 items-center justify-center mr-2 text-white border-white border-[1px] bg-primary w-full h-[40px]" to={`/marketplace/${item.cardId}`}>
                                                View Listing
                                            </Link>
                                        </div>
                                    }

                                    {
                                        item.state === 'cancelled' &&
                                        <div className="w-full text-sm">
                                            <button className="text-[#9CA3AF] border-[1px] border-[#9CA3AF] w-full h-[40px]" disabled>
                                                Offer Cancelled
                                            </button>
                                            <Link className="flex mt-3 items-center justify-center mr-2 text-white border-white border-[1px] bg-primary w-full h-[40px]" to={`/marketplace/${item.cardId}`}>
                                                View Listing
                                            </Link>
                                        </div>
                                    }
                                </div>
                            )
                        }
                    </div>

                    <div className="mt-9">
                        <Pagination itemsPerPage={3} totalItems={items.length} onPageChange={(value) => setCurrentMobilePage(value)} />
                    </div>
                </div>
            </>
            :
            <div className="mt-6 w-full text-center text-white text-base">
                No offers sent
            </div>
    )
}

const ReceivedItems = ({ items }) => {
    const [currentPage, setCurrentPage] = useState(1)
    const [currentMobilePage, setCurrentMobilePage] = useState(1)
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [showAcceptedModal, setShowAcceptedModal] = useState(false)
    const [acceptedItem, setAcceptedItem] = useState(null)
    const [userData, setUserData] = useAtom(userAtom)
    const { getUpdatedUserData } = useAuth()

    const sendNotification = async () => {
        await addDoc(collection(db, "messages"), {
            text: "Your offer was accepted.",
            senderId: acceptedItem.receiver,
            recipientId: acceptedItem.sender,
            timestamp: new Date(),
            state: 'unread'
        })

        if (!userData.recipients || userData.recipients.findIndex(item => item.id === acceptedItem.sender) === -1) {
            await addRecipient(acceptedItem.receiver, acceptedItem.sender)
            await addRecipient(acceptedItem.sender, acceptedItem.receiver)
            await getUpdatedUserData()
        }
    }

    const acceptOffer = async (item) => {
        setAcceptedItem(item)
        setShowConfirmModal(true)
        await getUpdatedUserData()
    }

    const confirmAcception = async () => {
        await updateOffer({ ...acceptedItem, state: 'accepted' })
        await sendNotification()
        setShowConfirmModal(false)
        setShowAcceptedModal(true)
    }

    return (
        <>
            {
                acceptedItem &&
                <DialogConfirmation
                    message={`Yes, I want to accept the $ ${acceptedItem.offer_price} offer.`}
                    open={showConfirmModal}
                    onButtonClick={confirmAcception}
                    onClose={() => setShowConfirmModal(false)}
                    buttonText="Accept Now"
                    title="Accept Offer?"
                    iconSrc="/assets/icons/icon-info.svg"
                    type="confirm"
                />
            }

            {
                acceptedItem &&
                <DialogConfirmation
                    message={`Successfully accepted customers $ ${acceptedItem.offer_price} offer.`}
                    open={showAcceptedModal}
                    onClose={() => setShowAcceptedModal(false)}
                    buttonText="Done"
                    title="Offer Accepted!"
                    iconSrc="/assets/icons/icon-check-circle.svg"
                    type="confirm"
                />
            }

            {
                items.length > 0 ?
                    <>
                        <div className="max-lg:hidden">
                            <div className="w-full overflow-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-[#2E2E2E] border-[1px] border-gray py-3 text-sm text-[#717171]">
                                            {
                                                fields.map((item, index) =>
                                                    <th className={`py-3 text-left ${index > 0 ? '' : 'pl-3'}`} key={`offer-field-${item}`}>
                                                        {item}
                                                    </th>
                                                )
                                            }
                                        </tr>
                                    </thead>

                                    <div className="pt-1" />
                                    <tbody className="text-white space-y-1 text-sm">
                                        {
                                            items.slice(4 * (currentPage - 1), 4 * currentPage).map((item, index) =>
                                                <tr key={`received-item-${item.title}-${item.id}-${currentPage}-${index}`} className="border-[1px] border-gray text-nowrap">
                                                    <td className="py-2 pl-3 pr-20 lg:min-w-[400px] min-w-[200px]">
                                                        <div className="flex items-center">
                                                            <img
                                                                src={item.picture}
                                                                alt="picture"
                                                                className="w-[50px] h-[50px] mr-6 border-[1px] border-gray"
                                                            />

                                                            <span className="uppercase text-sm">
                                                                {item.title}
                                                            </span>
                                                        </div>
                                                    </td>

                                                    <td className="lg:min-w-[200px] pr-10">
                                                        {formatDate(item.created_at.seconds)}
                                                    </td>

                                                    <td className="lg:min-w-[200px] pr-10">
                                                        $ {item.offer_price.toFixed(2)}
                                                    </td>

                                                    <td className="text-primary lg:min-w-[200px] pr-10">
                                                        $ {item.original_price.toFixed(2)}
                                                    </td>

                                                    <td className="pr-3">
                                                        {
                                                            item.state === 'pending' &&
                                                            <div className="flex">
                                                                <button className="solo-button mr-2 text-white border-white border-[1px] bg-[#3CBE2E] w-[170px] h-[45px]" onClick={() => acceptOffer(item)}>
                                                                    Accept
                                                                </button>

                                                                <button className="solo-button text-white  bg-[#F33E2F] w-[170px] h-[45px]" onClick={() => updateOffer({ ...item, state: 'declined' })}>
                                                                    Decline
                                                                </button>
                                                            </div>
                                                        }

                                                        {
                                                            item.state === 'completed' &&
                                                            <button className="text-[#3CBE2E] w-[345px] h-[45px] border-[#3CBE2E] border-[1px]" disabled>
                                                                Completed
                                                            </button>
                                                        }

                                                        {
                                                            item.state === 'accepted' &&
                                                            <button className="text-[#3CBE2E] w-[345px] h-[45px] bg-black border-[1px] border-white" disabled>
                                                                Accepted
                                                            </button>
                                                        }

                                                        {
                                                            item.state === 'declined' &&
                                                            <button className="text-[#F33E2F] w-[345px] h-[45px] bg-black border-[1px] border-white" disabled>
                                                                Declined
                                                            </button>
                                                        }
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                </table>
                            </div>

                            <div className="mt-9">
                                <Pagination itemsPerPage={4} totalItems={items.length} onPageChange={(value) => setCurrentPage(value)} />
                            </div>
                        </div>

                        <div className="lg:hidden">
                            <div className="grid sm:grid-cols-2 grid-cols-1 gap-5">
                                {
                                    items.slice(3 * (currentMobilePage - 1), 3 * currentMobilePage).map(item =>
                                        <div className="p-[10px] border-[1px] border-gray bg-[#191919] text-sm" key={`received-mobile-item-${item.title}-${item.id}-${currentPage}`}>
                                            <div className="flex w-full">
                                                <img src={item.picture} className="w-[113px] h-[113px] object-cover border-[1px] border-gray mr-4" alt="image" />

                                                <div className="w-full mb-9">
                                                    <div className="uppercase text-white text-sm">{item.title}</div>

                                                    <div className="mt-4 w-full flex justify-between text-[#717171] text-xs">
                                                        <span>Offer Price</span>
                                                        <span className="text-white">$ {item.offer_price.toFixed(2)}</span>
                                                    </div>

                                                    <div className="mt-3 w-full flex justify-between text-[#717171] text-xs">
                                                        <span>Original Price</span>
                                                        <span className="text-white">$ {item.original_price.toFixed(2)}</span>
                                                    </div>

                                                    <div className="mt-3 w-full flex justify-between text-[#717171] text-xs">
                                                        <span>Offer Date</span>
                                                        <span className="text-white">{formatDate(item.created_at.seconds)}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {
                                                item.state === 'pending' &&
                                                <div>
                                                    <button className="mr-2 text-white bg-[#3CBE2E] w-full h-[40px]" onClick={() => acceptOffer(item)}>
                                                        Accept
                                                    </button>

                                                    <button className="text-white mt-3 bg-[#F33E2F] w-full h-[40px] border-white border-[1px] " onClick={() => updateOffer({ ...item, state: 'declined' })}>
                                                        Decline
                                                    </button>
                                                </div>
                                            }

                                            {
                                                item.state === 'accepted' &&
                                                <button className="text-[#3CBE2E] w-full h-[40px] bg-black border-[1px] border-white" disabled>
                                                    Accepted
                                                </button>
                                            }

                                            {
                                                item.state === 'completed' &&
                                                <button className="text-[#3CBE2E] w-full h-[40px] border-[1px] border-[#3CBE2E]" disabled>
                                                    Completed
                                                </button>
                                            }

                                            {
                                                item.state === 'declined' &&
                                                <button className="text-[#F33E2F] w-full h-[40px] bg-black border-[1px] border-white" disabled>
                                                    Declined
                                                </button>
                                            }
                                        </div>
                                    )
                                }
                            </div>

                            <div className="mt-9">
                                <Pagination itemsPerPage={3} totalItems={items.length} onPageChange={(value) => setCurrentMobilePage(value)} />
                            </div>
                        </div>
                    </>
                    :
                    <div className="w-full text-center text-white text-lg">
                        No offers received
                    </div>
            }
        </>
    )
}

const Offers = () => {
    const { user, getUpdatedUserData } = useAuth()
    const [initialData, setUserInitialData] = useState()
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState('sent')
    const [sentItems, setSentItems] = useState([])
    const [receivedItems, setReceivedItems] = useState([])
    const [userData,] = useAtom(userAtom)
    const [lastUpdatedAt, setLastUpdatedAt] = useState();

    useEffect(() => {
        if (userData) {
            getSentOffersByUserId(userData.id).then((data) => {
                setSentItems(data)
            })
            getReceivedOffersByUserId(userData.id).then((data) => {
                setReceivedItems(data)
            })
        }
    }, [userData]);

    useEffect(() => {
        if (user) {
            if (activeTab === 'sent') {
                getSentOffersByUserId(user.uid).then((data) => {
                    setSentItems(data)
                })
            } else {
                getReceivedOffersByUserId(user.uid).then((data) => {
                    setReceivedItems(data)
                })
            }
        }
    }, [activeTab, lastUpdatedAt])

    return (
        // !loading &&
        <div>
            <ProfileTitle />

            <ProfileTabs activeTab="Offers" />

            <div className="lg:flex items-center justify-between gap-6 mt-9 mb-6">
                <span className="block uppercase text-primary text-[12px] max-lg:mb-8">Offers</span>

                <div className="flex">
                    <button
                        className={`w-[208px] h-[45px] mr-4 text-sm border-[1px] ${activeTab === 'sent' ? 'border-primary text-black bg-white' : 'text-white  border-gray bg-[#1D1D1D] '}`}
                        onClick={() => setActiveTab('sent')}
                    >
                        Offers Sent
                    </button>

                    <button
                        className={`w-[208px] h-[45px] text-sm border-[1px] ${activeTab === 'received' ? 'border-primary text-black bg-white' : 'text-white  border-gray bg-[#1D1D1D] '}`}
                        onClick={() => setActiveTab('received')}
                    >
                        Offers Received
                    </button>
                </div>
            </div>


            {
                activeTab === 'sent' ?
                    <div className="w-full overflow-auto">
                        <SentItems items={sentItems} reload={()=>setLastUpdatedAt(new Date())} />
                    </div>
                    :
                    <div className="w-full overflow-auto">
                        <ReceivedItems items={receivedItems} />
                    </div>
            }
        </div>
    )
}

export default Offers
